import React, { FC, useCallback, useMemo, useRef, useState } from 'react'
import { Typography } from '@mui/material'
import { Formik, FormikProps } from 'formik'

import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { DialogActions } from '@e3dc-react/shell/Elements/Dialog/Dialog'
import { useChangeInstallationOwner, useChangeInstallationOwnerFormInitializer } from 'src/Libs/installation-details/hooks'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import Form from './Form'
import moment from 'moment'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { FetchErrorCodes } from '@e3dc-react/shell/Libs'
import { Installation } from 'src/Libs/installations/models'
import { formatISO } from '@e3dc-react/shell/Libs/date'
import { OWNER_CHANGE_MIN_DATE } from 'src/Libs/installation-details/const'
import type { ChangeInstallationOwnerFormData } from './types'

export interface ChangeInstallationOwnerModalProps {
  onClose?: () => void
  installation: Installation | null
  oldOwnerAccountLinkId: number | null
  refetchData: () => void
}

/**
 * Displays modal for change installation owner
 * @param onClose - function to close modal
 * @param oldOwnerAccountLinkId - old owner id(account link id)
 * @param installationId - installation id
 * @param refetchData - function to refetch full installation data with connected models
 */
const ChangeInstallationOwnerModal: FC<ChangeInstallationOwnerModalProps> = ({ onClose, oldOwnerAccountLinkId, installation, refetchData }) => {
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const { t } = useCustomTranslation()
  const today = useMemo(() => new Date(), [])
  const { initialValues, validationSchema } = useChangeInstallationOwnerFormInitializer(today)

  const onChangeOwnerError = (error?: AuthFetchError): void => {
    if (error?.response?.data.errno === FetchErrorCodes.InstallationHasSameOwner) {
      enqueueErrorStack(t('installation.details.info.change-access-owner.same-owner-error-message'))
      return
    }

    enqueueErrorStack(t('installation.details.info.change-access-owner.error-message'))
  }

  const { mutate: changeInstallationOwner } = useChangeInstallationOwner({
    onSuccess: () => {
      enqueueSuccessStack(t('installation.details.info.change-access-owner.success-message'))
      refetchData()
      onClose?.()
    },
    onError: error => onChangeOwnerError(error as AuthFetchError),
  })

  const [isFormValid, setIsFormValid] = useState<boolean>(true)
  const formRef = useRef<FormikProps<ChangeInstallationOwnerFormData>>(null)

  const onSaveButtonHandler = (): void => {
    formRef.current?.handleSubmit()
  }

  const dialogActions: DialogActions = {
    primary: {
      name: t('installation.details.info.change-access-owner.submit-button-title'),
      onClick: onSaveButtonHandler,
      disabled: !isFormValid,
    },
    secondary: [
      {
        name: t('installation.details.info.change-access-owner.cancel-button-title'),
        onClick: () => onClose?.(),
      },
    ],
  }

  const onChangeFormValid = useCallback(
    (isFormValid: boolean): void => {
      setIsFormValid(isFormValid)
    },
    [setIsFormValid]
  )

  const onSubmitForm = (values: ChangeInstallationOwnerFormData): void => {
    const { userId, reason, startAt } = values
    if (!installation?.id) {
      onChangeOwnerError()
      return
    }
    const startAtString = formatISO(startAt)
    changeInstallationOwner({
      installationId: installation?.id,
      oldOwnerAccountLinkId: oldOwnerAccountLinkId ?? undefined,
      newOwnerId: Number.parseInt(userId),
      startAt: startAtString,
      reason,
    })
  }

  return (
    <Dialog title={t('installation.details.info.change-access-owner.dialog-title')} actions={dialogActions} maxWidth="sm" disableClose>
      <Typography variant="subtitle2">{t('installation.details.info.change-access-owner.dialog-text')}</Typography>
      <Typography variant="h3" mt={4} mb={2}>
        {t('installation.details.info.change-access-owner.user-field-title')}
      </Typography>
      <Formik initialValues={initialValues} onSubmit={onSubmitForm} validationSchema={validationSchema} innerRef={formRef} enableReinitialize>
        <Form
          onChangeFormValid={onChangeFormValid}
          installation={installation ?? undefined}
          maxDate={moment(today)}
          minDate={moment(OWNER_CHANGE_MIN_DATE)}
        />
      </Formik>
    </Dialog>
  )
}

export default ChangeInstallationOwnerModal
